import React from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { ComGate } from '@sga/shopify-payments-types'

import { Banner, Button, Card, Form, FormLayout, Heading, Stack, TextContainer } from '@shopify/polaris'

import { createApi } from '../api'
import { getTranslation } from '../util/getTranslation'
import { useAuth } from './AuthContext'
import ComGateCredentialsGuide from './ComGateCredentialsGuide'
import { FormTextField } from './FormTextField'

type TProps = {
  onSuccess(): void
}

export function ComGateCredentialsForm({ onSuccess }: TProps) {
  const config = useAuth()
  const { segmentCredentials } = config.account as ComGate.TSegmentAccount
  const form = useForm<ComGate.TSegmentCredentials>({
    reValidateMode: 'onBlur',
    defaultValues: {
      merchant: segmentCredentials.merchant,
      secret: segmentCredentials.secret,
    },
  })

  const [status, setStatus] = React.useState<'init' | 'failed' | 'done'>('init')

  const onSubmit = form.handleSubmit(async (values) => {
    setStatus('init')
    const api = createApi()
    const { success } = await api.verifyCredentials(values)
    if (success) {
      setStatus('done')
      onSuccess()
    } else {
      setStatus('failed')
    }
  })

  return (
    <Card sectioned title={getTranslation('comgate.credentials_title')}>
      <Stack vertical>
        <TextContainer>
          <p>{getTranslation('comgate.credentials_description')}</p>
        </TextContainer>
        <ComGateCredentialsGuide />
        <FormProvider {...form}>
          <Form onSubmit={onSubmit}>
            <FormLayout>
              <FormTextField name="merchant" label={getTranslation('comgate.shop_connection_identifier')} />
              <FormTextField name="secret" label={getTranslation('common.password')} />
              <Stack distribution="trailing">
                <Button primary submit disabled={form.formState.isSubmitting}>
                  {getTranslation('common.verify_details')}
                </Button>
              </Stack>
              {status === 'failed' && (
                <Banner status="critical">
                  <Heading>{getTranslation('comgate.verification_failed')}</Heading>
                  <p>{getTranslation('comgate.verification_failed_description')}</p>
                </Banner>
              )}
            </FormLayout>
          </Form>
        </FormProvider>
      </Stack>
    </Card>
  )
}
